import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Cloud, Loader2, RefreshCw, Unplug, AlertTriangle, FolderOpen, Info, Plus, X, Moon, Copy } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FolderPicker } from "@/components/FolderPicker";
import { OneDriveQuota } from "@/components/OneDriveQuota";
import { ConnectionCheck } from "@/components/ConnectionCheck";
import { AzureSetupGuide } from "@/components/AzureSetupGuide";
import { api, formatApiError } from "@/lib/api";
import { clearRestoreCache } from "@/lib/restore";

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
export const summaryText = (sum) => {
  const parts = [];
  if (sum.added) parts.push(`Added ${plural(sum.added, "song")}`);
  if (sum.removed) parts.push(`removed ${sum.removed}`);
  const base = parts.length ? parts.join(", ") : sum.updated ? `No new songs — ${plural(sum.updated, "song")} re-checked` : "Scan finished — nothing changed";
  return sum.skipped_folders ? `${base} · ${plural(sum.skipped_folders, "folder")} skipped (OneDrive errors)` : base;
};

// Full scan: listing folders ≈ first 35% (estimated from the previous scan), reading tags = the rest.
export const scanProgress = (s) => {
  if (s.sync_mode === "delta") return s.scanned_files ? Math.min(99, Math.round((s.synced_tracks / s.scanned_files) * 100)) : null;
  if (s.scanned_files) return Math.min(99, 35 + Math.round((s.synced_tracks / s.scanned_files) * 65));
  if (s.expected_folders) return Math.min(34, Math.round((Math.min(s.scanned_folders || 0, s.expected_folders) / s.expected_folders) * 35));
  return null;
};

const elapsed = (iso) => {
  if (!iso) return "";
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${String(sec % 60).padStart(2, "0")}s`;
};

const fmtLeft = (sec) => (sec < 60 ? "under a minute left" : sec < 3600 ? `~${Math.round(sec / 60)} min left` : `~${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m left`);

// Rolling rate over the last ~45s of samples (position = songs or folders done) → seconds remaining.
const useEta = (s) => {
  const samples = useRef([]);
  const tagPhase = !!s.scanned_files;
  const pos = tagPhase ? s.synced_tracks || 0 : s.scanned_folders || 0;
  const total = tagPhase ? s.scanned_files : s.expected_folders;
  const phase = `${s.sync_started_at}|${tagPhase ? "tags" : "folders"}`;
  const [eta, setEta] = useState(null);
  useEffect(() => {
    if (s.sync_status !== "running") { samples.current = []; setEta(null); return; }
    const now = Date.now();
    if (samples.current.length && samples.current[0].phase !== phase) samples.current = [];
    samples.current.push({ t: now, pos, phase });
    samples.current = samples.current.filter((x) => now - x.t <= 45000);
    const first = samples.current[0];
    const span = (now - first.t) / 1000;
    const rate = span >= 8 ? (pos - first.pos) / span : 0;
    setEta(total && rate > 0 ? Math.max(0, (total - pos) / rate) : null);
  }, [pos, total, phase, s.sync_status]);
  return eta;
};

const SyncStatus = ({ s }) => {
  const eta = useEta(s);
  if (s.sync_status === "running") {
    const stage = s.sync_mode === "delta" ? (s.scanned_files ? `checking ${s.synced_tracks} / ${s.scanned_files} changed file${s.scanned_files === 1 ? "" : "s"}` : "asking OneDrive what changed")
      : s.scanned_files ? `reading tags ${s.synced_tracks} / ${s.scanned_files} songs`
      : `listing folders — ${s.scanned_folders || 0}${s.expected_folders ? ` of ~${s.expected_folders}` : ""} folder${s.scanned_folders === 1 ? "" : "s"} checked, ${s.found_files || 0} song${s.found_files === 1 ? "" : "s"} found`;
    const pct = scanProgress(s);
    return (
      <div className="min-w-[18rem] text-zinc-700" data-testid="onedrive-sync-progress">
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Scanning… {stage}</span>
          <span className="shrink-0 font-mono text-xs text-zinc-500" data-testid="onedrive-sync-percent">{pct === null ? elapsed(s.sync_started_at) : `${pct}% · ${elapsed(s.sync_started_at)}`}{eta !== null && <span className="text-zinc-700" data-testid="onedrive-sync-eta"> · {fmtLeft(eta)}</span>}</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-100" role="progressbar" aria-valuenow={pct ?? undefined} aria-valuemin={0} aria-valuemax={100}>
          {pct === null
            ? <div className="h-full w-1/3 animate-[scanSlide_1.4s_ease-in-out_infinite] rounded-full bg-zinc-900/70" data-testid="onedrive-sync-indeterminate" />
            : <div className="h-full rounded-full bg-zinc-900 transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="onedrive-sync-bar" />}
        </div>
      </div>
    );
  }
  if (s.sync_status === "error") return <span className="flex items-center gap-2 text-red-600"><AlertTriangle size={14} />{s.sync_error}</span>;
  if (s.last_synced_at) return (
    <span className="text-zinc-600">{s.synced_albums} albums · {s.synced_tracks} tracks{s.wishlist_found ? <span className="text-amber-700" data-testid="onedrive-wishlist-found"> · {s.wishlist_found} wishlist songs found</span> : null}
      <span className="font-mono text-xs text-zinc-400"> · last sync {new Date(s.last_synced_at).toLocaleString()}</span>
      {s.last_sync_summary && <span className="block text-xs text-zinc-500" data-testid="onedrive-last-summary">{summaryText(s.last_sync_summary)} · {s.last_sync_summary.mode === "delta" ? "quick scan" : "full scan"}</span>}
    </span>
  );
  return <span className="text-zinc-500">Not synced yet.</span>;
};

export const OneDriveSection = ({ onChange }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [picker, setPicker] = useState(false);
  const [check, setCheck] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/onedrive").then((r) => setS(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (s) onChange?.(s); }, [s, onChange]);
  useEffect(() => {
    if (params.get("onedrive") === "connected") { toast.success("OneDrive connected"); setParams({}, { replace: true }); }
    if (params.get("onedrive_error")) {
      const msg = params.get("onedrive_error");
      const hint = /redirect_uri/i.test(msg) ? " — register the redirect URI shown below in your Microsoft app, then try again." : "";
      toast.error(`OneDrive: ${msg}${hint}`, { duration: 12000 });
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  useEffect(() => {
    if (s?.sync_status !== "running") return;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [s?.sync_status, load]);
  const prevStatus = useRef(null);
  useEffect(() => {
    const was = prevStatus.current;
    prevStatus.current = s?.sync_status;
    if (was !== "running" || !s || s.sync_status === "running") return;
    if (s.sync_status === "done" && s.last_sync_summary) toast.success(summaryText(s.last_sync_summary), { description: s.last_sync_summary.mode === "delta" ? "Quick scan — only files changed since last time were checked." : "Full scan of your library folders.", duration: 8000 });
    else if (s.sync_status === "error") toast.error(`Scan failed: ${s.sync_error}`);
  }, [s]);

  const run = async (name, fn) => { setBusy(name); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } setBusy(""); };
  const connect = () => run("connect", async () => { const { data } = await api.get("/onedrive/login"); window.location.href = data.url; });
  const pick = (folder) => run("folder", async () => {
    setPicker(false);
    const { data } = await api.post("/onedrive/library-folders", folder);
    setS(data);
    toast.success(`Added ${folder.path || "/"} — scanning now`);
  });
  const removeFolder = (f) => run("remove", async () => {
    if (!window.confirm(`Remove ${f.path || "/"} from your library? Files in OneDrive are untouched.`)) return;
    const { data } = await api.delete(`/onedrive/library-folders/${f.id}`);
    setS(data);
  });
  const toggleNightly = (v) => run("prefs", async () => { const { data } = await api.put("/onedrive/prefs", { nightly_sync: v }); setS(data); });
  const sync = (full = false) => run("sync", async () => { const { data } = await api.post(`/onedrive/sync${full ? "?full=true" : ""}`); setS(data); });
  const cancelSync = () => run("cancel", async () => { const { data } = await api.post("/onedrive/sync/cancel"); setS(data); toast("Stopping the scan…"); });
  const disconnect = () => run("disconnect", async () => {
    if (!window.confirm("Disconnect OneDrive? Its songs will be removed from your library (files in OneDrive are untouched).")) return;
    const { data } = await api.delete("/onedrive"); clearRestoreCache(); setS(data);
  });

  if (!s) return <Loader2 className="animate-spin text-zinc-400" />;
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-8" data-testid="onedrive-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700"><Cloud size={18} /></div>
          <div>
            <h2 className="text-lg font-medium">OneDrive</h2>
            <p className="text-xs text-zinc-500">Stream music straight from a OneDrive folder. Nothing is copied.</p>
          </div>
        </div>
        {s.connected && (
          <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${s.needs_reconnect ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="onedrive-status-badge">
            <span className={`h-1.5 w-1.5 rounded-full ${s.needs_reconnect ? "bg-red-500" : "bg-emerald-500"}`} />{s.needs_reconnect ? "Reconnect needed" : "Connected"}
          </span>
        )}
      </div>

      {!s.configured && (
        <div className="mt-6 rounded-md bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="onedrive-not-configured">
          <p className="flex gap-2"><Info size={14} />OneDrive app credentials are missing on the server (ONEDRIVE_CLIENT_ID / ONEDRIVE_CLIENT_SECRET).</p>
          <div className="mt-2 pl-6"><AzureSetupGuide redirectUri={s.redirect_uri} /></div>
        </div>
      )}

      {s.configured && (!s.connected || s.needs_reconnect) && s.redirect_uri && (
        <div className="mt-6 rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600" data-testid="onedrive-redirect-help">
          <p className="flex items-center gap-2"><Info size={13} className="shrink-0" />Sign-in only works if this exact redirect URI is registered for your Microsoft app (Azure Portal → App registrations → Authentication → Web):</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-white px-2 py-1 font-mono text-[11px] text-zinc-800" data-testid="onedrive-redirect-uri">{s.redirect_uri}</code>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(s.redirect_uri); toast.success("Redirect URI copied"); }}
              className="flex h-7 items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 text-[11px] hover:bg-zinc-100" data-testid="onedrive-redirect-copy"><Copy size={11} />Copy</button>
          </div>
          <ConnectionCheck autoRun onResult={setCheck} />
          <div className="mt-3 border-t border-zinc-200 pt-3"><AzureSetupGuide redirectUri={s.redirect_uri} /></div>
        </div>
      )}

      {!s.connected || s.needs_reconnect ? (
        <button onClick={connect} disabled={!s.configured || !!busy} data-testid="onedrive-connect-button"
          className="mt-8 flex h-11 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm text-white transition-transform hover:scale-[1.02] disabled:opacity-40">
          {busy === "connect" ? <Loader2 size={15} className="animate-spin" /> : <Cloud size={15} />}{s.needs_reconnect ? "Reconnect OneDrive" : "Connect OneDrive"}
          {s.configured && (
            <span data-testid="onedrive-connect-dot" data-status={check === null ? "checking" : check.ok ? "ok" : "fail"}
              title={check === null ? "Checking Microsoft app settings…" : check.ok ? "Microsoft app settings look right" : "Microsoft app settings need fixing — see the test above"}
              className={`ml-1 h-2 w-2 rounded-full ${check === null ? "animate-pulse bg-zinc-400" : check.ok ? "bg-emerald-400" : "bg-red-500"}`} />
          )}
        </button>
      ) : null}

      {s.connected && (
        <>
          <div className="mt-6 rounded-md bg-zinc-50 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Account</p>
            <p className="mt-1 truncate text-sm" data-testid="onedrive-account">{s.account_name} <span className="font-mono text-xs text-zinc-500">{s.account_email}</span></p>
          </div>
          {!s.needs_reconnect && <OneDriveQuota refreshKey={s.last_synced_at} />}
          <div className="mt-6">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Music folders · {s.folders.length}</p>
            {s.folders.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="onedrive-no-folders">No folders yet. Add one to start building your library.</p>
            ) : (
              <div className="divide-y divide-zinc-100 rounded-md border border-zinc-200" data-testid="onedrive-folder-list">
                {s.folders.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`onedrive-folder-${f.id}`}>
                    <FolderOpen size={15} className="text-amber-600" />
                    <span className="flex-1 truncate font-mono text-sm">{f.path || "/"}</span>
                    <button onClick={() => removeFolder(f)} disabled={!!busy || s.sync_status === "running"} data-testid={`onedrive-folder-remove-${f.id}`}
                      className="rounded p-1 text-zinc-400 transition-colors hover:text-red-600 disabled:opacity-30" title="Remove folder"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button onClick={() => setPicker(true)} disabled={!!busy || s.sync_status === "running"} data-testid="onedrive-add-folder-button"
              className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 px-5 text-sm hover:border-zinc-900 disabled:opacity-40">
              <Plus size={14} />Add folder
            </button>
            <button onClick={disconnect} disabled={!!busy} data-testid="onedrive-disconnect-button" className="flex h-10 items-center gap-2 rounded-full px-4 text-sm text-zinc-500 hover:text-red-600"><Unplug size={14} />Disconnect</button>
          </div>
          {s.folders.length > 0 && (
            <div className="mt-8 border-t border-zinc-200 pt-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1 text-sm" data-testid="onedrive-sync-status"><SyncStatus s={s} /></div>
                {s.sync_status === "running" ? (
                  <button onClick={cancelSync} disabled={!!busy} data-testid="onedrive-sync-cancel-button"
                    className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 px-5 text-sm text-zinc-700 hover:bg-zinc-100 disabled:opacity-50">
                    <X size={14} />Stop scan
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    {s.has_delta && (
                      <button onClick={() => sync(true)} disabled={!!busy} data-testid="onedrive-full-rescan-button" title="Re-list every folder instead of only the changes"
                        className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 px-4 text-sm text-zinc-700 hover:bg-zinc-100 disabled:opacity-50">
                        Full rescan
                      </button>
                    )}
                    <button onClick={() => sync(false)} disabled={!!busy} data-testid="onedrive-sync-button"
                      className="flex h-10 items-center gap-2 rounded-full bg-amber-600 px-5 text-sm text-white hover:bg-amber-700 disabled:opacity-50">
                      <RefreshCw size={14} />{s.has_delta ? "Quick sync" : "Sync now"}
                    </button>
                  </div>
                )}
              </div>
              <label className="mt-5 flex items-center justify-between gap-4 rounded-md bg-zinc-50 px-4 py-3">
                <span>
                  <span className="flex items-center gap-2 text-sm font-medium"><Moon size={14} />Nightly sync</span>
                  <span className="text-xs text-zinc-500">Re-scan all folders every night at 3:00 UTC so new albums appear on their own.{s.last_sync_trigger === "nightly" ? " Last sync ran automatically." : ""}</span>
                </span>
                <Switch checked={s.nightly_sync} onCheckedChange={toggleNightly} disabled={busy === "prefs"} data-testid="onedrive-nightly-switch" />
              </label>
            </div>
          )}
        </>
      )}
      <FolderPicker open={picker} onOpenChange={setPicker} onPick={pick} />
    </section>
  );
};

